import React from 'react';
import { useTranslation } from 'react-i18next';
import { formClasses } from 'styles/tailwindClassnames';
import { Source } from 'shared/types/ObjectTypes';
import { Icon } from 'react-icons-kit';
import { ic_close } from 'react-icons-kit/md/ic_close';

// Read-only slide-out for a single device (GUNDI-5178). Same shell conventions
// as AddDestinationDrawer. Creating a device lives in AddDeviceModal, shared
// with the device-filter editor, so both entry points keep one form.

type SourceDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
  source: Source | null;
};

const SourceDrawer = ({ isOpen, onClose, source }: SourceDrawerProps) => {
  const { t } = useTranslation();

  return (
    <div
      data-testid="SourceDrawer-panel"
      className={`fixed right-0 top-0 z-[56] flex h-full w-full flex-col border-l border-ui-divider-lines bg-white shadow-xl transition-transform duration-300 md:z-[60] md:w-[420px] ${isOpen ? 'translate-x-0' : 'pointer-events-none translate-x-full'}`}
      aria-hidden={!isOpen}
      // Mounted while closed so it can slide; inert keeps it out of the tab
      // order. Not typed on React 18's HTMLAttributes yet, hence the cast.
      {...({ inert: isOpen ? undefined : '' } as any)}
    >
      <div className="flex items-center justify-between border-b border-ui-divider-lines px-4 py-4">
        <h2 className="text-body-lg font-bold text-ui-text-primary-text">
          {t('Source')}
        </h2>
        <button
          onClick={onClose}
          aria-label={t('Close')}
          className="flex h-9 w-9 items-center justify-center text-ui-text-secondary-text hover:text-ui-text-primary-text"
          data-testid="SourceDrawer-close"
        >
          <Icon icon={ic_close} size={24} />
        </button>
      </div>

      <div className="flex flex-1 flex-col gap-4 overflow-y-auto px-4 py-4">
        {source && (
          <>
            <div className="flex flex-col gap-1">
              <span className={formClasses.label}>{t('Source ID')}</span>
              <span
                className="break-all text-body-sm text-ui-text-primary-text"
                data-testid="SourceDrawer-view-id"
              >
                {source.external_id}
              </span>
            </div>
            <div className="flex flex-col gap-1">
              <span className={formClasses.label}>{t('Name')}</span>
              <span className="break-words text-body-sm text-ui-text-primary-text">
                {source.name ? t('"{{name}}"', { name: source.name }) : '—'}
              </span>
            </div>
            <div className="flex flex-col gap-1">
              <span className={formClasses.label}>{t('Origin')}</span>
              <span
                className="text-body-sm text-ui-text-primary-text"
                data-testid="SourceDrawer-origin"
              >
                {source.created_via === 'manual'
                  ? t('Added by user')
                  : source.created_via === 'ingestion'
                    ? t('Discovered by the integration')
                    : t('Unknown')}
              </span>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SourceDrawer;
